import {
  CreateGDTTemplateRuleDto,
  GDTMessageGroup,
  QueryFactory,
  useEnabledDataBlockTypes,
  PatchGDTTemplateRuleDto,
  DataBlockVariant,
  GDTTemplateRuleDto,
} from '@lmt/shared-types';
import {
  DataGrid,
  DXG,
  RowInsertingEvent,
  RowRemovingEvent,
  RowUpdatingEvent,
  useDataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { AutoSizedTableWrapper } from '@lmt/unicorn-shared/app/components/common/AutoSizedTableWrapper';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Loading } from '../../../common/Loading';
import { visitInformationDataBlockTypeName } from '../../../measurements/common';
import { useGDTMessageGroupsList } from './common';
import { useEventCallback } from '@mui/material';
import {
  allowOnlySingleNewRow,
  useCachedDataSourceWhenEditing,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/grid-helper';

type Props = {
  subtenantId?: number;
};

const nameof = nameofFactory<GDTTemplateRuleDto>();

export const GDTTemplateRulesTable: FC<Props> = (props) => {
  if (!props.subtenantId) {
    return null;
  }
  const [isSaving, setIsSaving] = useState(false);
  const modals = useModal();
  const i18n = useScopedTranslation('gdt.template_rules');
  const i18nextGlobal = useTranslation();
  useDataGrid();
  const dataGridRef = useRef<DataGrid<GDTTemplateRuleDto, string>>(null);
  const dataBlockTypes = useEnabledDataBlockTypes();
  const rulesQuery = QueryFactory.GDTTemplateRulesQuery.useGetQuery(
    {
      subtenantId: props.subtenantId,
    },
    {
      enabled: !!props.subtenantId,
    },
  );
  const dataSource = useCachedDataSourceWhenEditing(
    dataGridRef,
    rulesQuery.data,
  );

  const { data: templates } =
    QueryFactory.GDTOutgoingTemplatesQuery.useGetQuery();

  const GDTMessageGroups = useGDTMessageGroupsList();
  const dataBlockTypeOptions = useMemo(() => {
    return dataBlockTypes
      .filter(
        (type) =>
          !type.isDeactivated &&
          type.variant !== DataBlockVariant.Patient &&
          type.variant !== DataBlockVariant.SharedFields &&
          !(
            type.variant === DataBlockVariant.Visit &&
            type.value === visitInformationDataBlockTypeName
          ),
      )
      .map((x) => {
        return {
          value: x.id,
          displayValue: x.value,
        };
      });
  }, [dataBlockTypes]);

  const onMessageGroupChange = useEventCallback(
    (data: GDTTemplateRuleDto, value: GDTMessageGroup) => {
      data.dataBlockTypeId = null;
      data.messageGroup = value;
    },
  );

  const onRowCreate = useEventCallback(
    (row: RowInsertingEvent<GDTTemplateRuleDto, string>) => {
      async function onRowCreate(data: GDTTemplateRuleDto) {
        const dto: CreateGDTTemplateRuleDto = {
          ...data,
        };
        try {
          setIsSaving(true);
          await QueryFactory.GDTTemplateRulesClient.create(dto);
          await rulesQuery.refetch();
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        } finally {
          setIsSaving(false);
        }
        return false;
      }

      row.cancel = onRowCreate(row.data);
    },
  );

  const onRowEdit = useEventCallback(
    (row: RowUpdatingEvent<GDTTemplateRuleDto, string>) => {
      async function onRowEdit(
        oldData: GDTTemplateRuleDto,
        data: Partial<GDTTemplateRuleDto>,
      ) {
        const rule = {
          ...oldData,
          ...data,
        };
        if (rule.id > 10000000) return false;

        const dto: PatchGDTTemplateRuleDto = {
          ...rule,
        };
        try {
          setIsSaving(true);
          await QueryFactory.GDTTemplateRulesClient.patch(rule.id, dto);
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        } finally {
          setIsSaving(false);
        }
        return false;
      }

      row.cancel = onRowEdit(row.oldData, row.newData);
    },
  );

  const onRowDelete = useEventCallback(
    (row: RowRemovingEvent<GDTTemplateRuleDto, string>) => {
      async function onRowDelete(row: GDTTemplateRuleDto) {
        const id = row.id;
        try {
          setIsSaving(true);
          await QueryFactory.GDTTemplateRulesClient.remove(id);
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        } finally {
          setIsSaving(false);
        }
        return false;
      }
      row.cancel = onRowDelete(row.data);
    },
  );

  return (
    <Loading
      loading={rulesQuery.isLoading || isSaving}
      title={i18n.t('loading')}
    >
      <AutoSizedTableWrapper>
        <DXG.DataGrid
          dataSource={dataSource}
          keyExpr={'id'}
          showBorders={true}
          style={{ width: '100%' }}
          ref={dataGridRef}
          onInitNewRow={(e) => {
            const data = e.data;
            data.isActive = true;
            data.subtenantId = props.subtenantId!;
            data.id = 10000000 + Math.round(Math.random() * 10000);
            data.isAutoSending = false;
          }}
          onEditingStart={(e) => {
            if (e.data.isDefault) {
              if (e.column?.dataField === nameof('messageGroup')) {
                e.cancel = true;
              }
              if (e.column?.dataField === nameof('dataBlockTypeId')) {
                e.cancel = true;
              }
              if (e.column?.dataField === nameof('documentTemplateId')) {
                e.cancel = true;
              }
            }
            if (e.data.messageGroup !== GDTMessageGroup.DataBlock) {
              if (e.column?.dataField === nameof('dataBlockTypeId')) {
                e.cancel = true;
              }
            }
          }}
          onRowInserting={onRowCreate}
          onRowUpdating={onRowEdit}
          onRowRemoving={onRowDelete}
          onToolbarPreparing={allowOnlySingleNewRow}
        >
          <DXG.Paging enabled={false} />
          <DXG.Editing
            mode="batch"
            allowUpdating={true}
            allowAdding={true}
            allowDeleting={true}
            selectTextOnEditStart={true}
            startEditAction="click"
            newRowPosition="pageTop"
          />
          <DXG.Column dataField="id" sortOrder="desc" visible={false} />
          <DXG.Column
            dataField={nameof('messageGroup')}
            caption={i18n.t('columns.message_group')}
            setCellValue={onMessageGroupChange}
          >
            <DXG.Lookup
              dataSource={GDTMessageGroups as any}
              valueExpr="value"
              displayExpr="displayValue"
            />
            <DXG.RequiredRule />
          </DXG.Column>
          <DXG.Column
            dataField={nameof('dataBlockTypeId')}
            caption={i18n.t('columns.data_block_type')}
          >
            <DXG.Lookup
              dataSource={dataBlockTypeOptions as any}
              valueExpr="value"
              displayExpr="displayValue"
            />
            <DXG.CustomRule
              message={i18n.t('data_block_type_required')}
              validationCallback={(item: any) => {
                const data = item.data as GDTTemplateRuleDto;
                if (data.messageGroup === GDTMessageGroup.DataBlock) {
                  return !!data.dataBlockTypeId;
                }
                return true;
              }}
            />
          </DXG.Column>
          <DXG.Column
            dataField={nameof('documentTemplateId')}
            caption={i18n.t('columns.template')}
            dataType="number"
          >
            <DXG.Lookup
              dataSource={(templates ?? [])
                .map((x) => ({
                  ...x,
                  title: x.isDefault
                    ? i18nextGlobal.t(x.title as any)
                    : x.title,
                }))
                .sort((a, b) => a.title.localeCompare(b.title))}
              displayExpr="title"
              valueExpr="id"
            />
            <DXG.RequiredRule />
          </DXG.Column>
          <DXG.Column
            width={160}
            dataField={nameof('isAutoSending')}
            dataType="boolean"
            caption={i18n.t('columns.auto_sending')}
          />
          <DXG.Column
            width={120}
            dataField={nameof('isActive')}
            dataType="boolean"
            caption={i18n.t('columns.is_active')}
          />
        </DXG.DataGrid>
      </AutoSizedTableWrapper>
      <DXG.Column type="buttons">
        <DXG.Button name="delete" />
      </DXG.Column>
    </Loading>
  );
};
